// The Plan picker (wireframes 4 and 5): one sheet with a Recipes tab and a
// Leftovers tab, opened from a meal's "+ Add a recipe", "+ Use leftovers"
// or a dish's "Change". On a phone and a narrow window it is a modal
// <dialog>, so focus stays inside it and Escape closes it; a sheet from the
// bottom on a phone (index.css). The desktop week (slice 5d) shows the same
// body as a panel beside the days that stays open (PickerPanel).
//
// Recipes: search, filters (under 30 min, in season, favorites), Dining
// out for a Main, then Favorites, Recently cooked and every recipe of the
// dish type. Leftovers: the dishes from the last 7 days and earlier the same
// day, then "Something older" (any recipe, unlinked).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import { COLORS, DINING_OUT, FONT_SIZE, LAYOUT, RADIUS, RECIPE_TYPE_LABELS, SPACE } from "../../lib/constants";
import { mealDate } from "../../lib/planScaling";
import { NO_FILTERS, filterRecipes, isFiltering, leftoverRows, recentlyCooked, recipeMeta, searchRecipes, seasonOf, type LeftoverRow, type RecipeFilters } from "../../lib/pickerView";
import type { Day, DishType, MealSlot, Recipe } from "../../lib/types";
import { smallLabel, supporting } from "../recipeStyles";
import { chip, inputStyle, roundButton, textButton } from "./planStyles";

export type PickerTab = "recipes" | "leftovers";

export interface PickerRequest {
  tab: PickerTab;
  dishType: DishType;
  // The dish types this pick may fill: just the one when changing a dish,
  // the free ones when adding.
  typeChoices: DishType[];
}

const badge = { fontSize: FONT_SIZE.xs, fontWeight: 700, color: COLORS.soft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.pill, padding: "0 var(--space-md)" } as const;

const plural = (dt: DishType) => (dt === "entree" ? "mains" : `${RECIPE_TYPE_LABELS[dt].toLowerCase()}s`);

export function PickerSheet({ day, slot, request, onClose }: { day: Day; slot: MealSlot; request: PickerRequest; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => dialog?.close();
  }, []);
  return (
    // The dialog's own Escape handling fires "cancel"; closing goes through onClose.
    <dialog
      ref={dialogRef}
      className="picker-sheet mp-sans"
      aria-labelledby="picker-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => e.target === dialogRef.current && onClose()}
    >
      <PickerBody day={day} slot={slot} request={request} onPicked={onClose} onClose={onClose} />
    </dialog>
  );
}

// The picker beside the desktop week: always open, filling the selected meal
// ("Filling: Friday dinner, main"). `extra` holds the selected meal's own
// actions (remove, move).
export function PickerPanel({ day, slot, request, onPicked, extra }: { day: Day; slot: MealSlot; request: PickerRequest; onPicked: () => void; extra?: ReactNode }) {
  return (
    <aside aria-labelledby="picker-title" className="mp-sans" style={{ display: "flex", flexDirection: "column", border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.xl, background: COLORS.card, overflow: "hidden", height: "100%" }}>
      <PickerBody day={day} slot={slot} request={request} onPicked={onPicked} extra={extra} />
    </aside>
  );
}

// Heading, dish-type choice, tabs and the tab's list. With `onClose` it has
// a close button (the sheet); without, it says what it is filling (the panel).
function PickerBody({ day, slot, request, onPicked, onClose, extra }: { day: Day; slot: MealSlot; request: PickerRequest; onPicked: () => void; onClose?: () => void; extra?: ReactNode }) {
  const mp = useMealPlanner();
  const [tab, setTab] = useState<PickerTab>(request.tab);
  const [dishType, setDishType] = useState<DishType>(request.dishType);
  const date = mealDate(mp.weekStart, day);
  const dish = RECIPE_TYPE_LABELS[dishType].toLowerCase();

  const pickRecipe = (id: string) => {
    mp.setDish(day, slot, dishType, id);
    onPicked();
  };
  const pickLeftovers = (choice: { source: LeftoverRow["candidate"] } | { recipeId: string }) => {
    if ("source" in choice) {
      const { weekStart, day: d, mealSlot, dishType: dt } = choice.source;
      mp.placeLeftovers(day, slot, dishType, { source: { weekStart, day: d, mealSlot, dishType: dt } });
    } else {
      mp.placeLeftovers(day, slot, dishType, { recipeId: choice.recipeId });
    }
    onPicked();
  };

  const tabButton = (t: PickerTab, label: string) => (
    <button
      type="button"
      role="tab"
      id={`picker-tab-${t}`}
      aria-selected={tab === t}
      aria-controls="picker-panel"
      onClick={() => setTab(t)}
      style={{
        flex: 1,
        minHeight: LAYOUT.tapTarget,
        border: "none",
        borderBottom: `3px solid ${tab === t ? COLORS.accent : "transparent"}`,
        background: "transparent",
        color: tab === t ? COLORS.accent : COLORS.soft,
        fontSize: FONT_SIZE.base,
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <>
      <div style={{ padding: "var(--space-xl) var(--space-xxl) 0", borderBottom: `1px solid ${COLORS.line}` }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE.xl }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {onClose ? (
              <>
                <div style={supporting}>{date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
                <h2 id="picker-title" style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h3, fontWeight: 600 }}>
                  Choose a {slot} {dish}
                </h2>
              </>
            ) : (
              <>
                <div style={{ ...smallLabel, color: COLORS.soft }}>Filling</div>
                <h2 id="picker-title" style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600 }}>
                  {date.toLocaleDateString(undefined, { weekday: "long" })} {slot}, {dish}
                </h2>
              </>
            )}
          </div>
          {onClose && (
            <button type="button" aria-label="Close" onClick={onClose} style={roundButton}>
              ×
            </button>
          )}
        </div>
        {extra}
        {/* A few dish types as buttons; more (adding to a planned meal) as a list. */}
        {request.typeChoices.length > 3 && (
          <label style={{ ...supporting, display: "flex", alignItems: "center", gap: SPACE.md, marginTop: SPACE.md }}>
            Dish
            <select value={dishType} onChange={(e) => setDishType(e.target.value as DishType)} style={{ ...inputStyle, flex: 1 }}>
              {request.typeChoices.map((dt) => (
                <option key={dt} value={dt}>
                  {dt === "entree" && request.typeChoices[0] === "entree" && request.typeChoices.length > 1 ? "Main (change it)" : `${RECIPE_TYPE_LABELS[dt]}${request.typeChoices[0] === "entree" ? " (add one)" : ""}`}
                </option>
              ))}
            </select>
          </label>
        )}
        {request.typeChoices.length > 1 && request.typeChoices.length <= 3 && (
          <div role="group" aria-label="Dish" style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm, marginTop: SPACE.md }}>
            {request.typeChoices.map((dt) => (
              <button key={dt} type="button" aria-pressed={dt === dishType} onClick={() => setDishType(dt)} style={chip(dt === dishType ? "accent" : "plain")}>
                {RECIPE_TYPE_LABELS[dt]}
              </button>
            ))}
          </div>
        )}
        <div role="tablist" aria-label="Fill this dish with" style={{ display: "flex", marginTop: SPACE.md }}>
          {tabButton("recipes", "Recipes")}
          {tabButton("leftovers", "Leftovers")}
        </div>
      </div>
      <div id="picker-panel" role="tabpanel" aria-labelledby={`picker-tab-${tab}`} style={{ flex: 1, overflowY: "auto", padding: "var(--space-xl) var(--space-xxl) var(--space-x4)" }}>
        {tab === "recipes" ? (
          <RecipesTab key={dishType} day={day} dishType={dishType} onPick={pickRecipe} />
        ) : (
          <LeftoversTab key={dishType} day={day} slot={slot} dishType={dishType} onPick={pickLeftovers} />
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------- recipes ---

function RecipesTab({ day, dishType, onPick }: { day: Day; dishType: DishType; onPick: (id: string) => void }) {
  const mp = useMealPlanner();
  const [filters, setFilters] = useState<RecipeFilters>(NO_FILTERS);
  const date = mealDate(mp.weekStart, day);
  const toggle = (k: "quick" | "inSeason" | "favorites") => setFilters((f) => ({ ...f, [k]: !f[k] }));
  const filtering = isFiltering(filters);
  const all = filterRecipes(mp.recipes, dishType, filters, date, mp.isFavorite);
  const favorites = filtering ? [] : all.filter(mp.isFavorite);
  const recent = filtering ? [] : recentlyCooked(mp.plansByWeek, dishType, mp.recipeById, new Date());

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <input
        type="search"
        aria-label={`Search your ${plural(dishType)}`}
        placeholder={`Search your ${plural(dishType)}`}
        value={filters.query}
        onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
        style={{ ...inputStyle, width: "100%" }}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm }}>
        {dishType === "entree" && (
          <button type="button" onClick={() => onPick(DINING_OUT)} style={{ ...chip("plain"), borderStyle: "dashed", color: COLORS.ink }}>
            Dining out
          </button>
        )}
        <button type="button" aria-pressed={filters.quick} onClick={() => toggle("quick")} style={chip(filters.quick ? "accent" : "plain")}>
          Under 30 min
        </button>
        <button type="button" aria-pressed={filters.inSeason} onClick={() => toggle("inSeason")} style={chip(filters.inSeason ? "accent" : "plain")} title={`Tagged ${seasonOf(date)}`}>
          In season
        </button>
        <button type="button" aria-pressed={filters.favorites} onClick={() => toggle("favorites")} style={chip(filters.favorites ? "accent" : "plain")}>
          Favorites
        </button>
      </div>

      {filtering ? (
        <Section title={`${all.length} ${all.length === 1 ? "recipe" : "recipes"}`}>
          {all.map((r) => (
            <RecipeRow key={r.id} recipe={r} onPick={onPick} />
          ))}
          {all.length === 0 && <p style={{ ...supporting, margin: 0 }}>Nothing matches. Try fewer filters.</p>}
        </Section>
      ) : (
        <>
          {favorites.length > 0 && (
            <Section title="Favorites">
              {favorites.map((r) => (
                <RecipeRow key={r.id} recipe={r} onPick={onPick} />
              ))}
            </Section>
          )}
          {recent.length > 0 && (
            <Section title="Recently cooked">
              {recent.map(({ recipe, when }) => (
                <RecipeRow key={recipe.id} recipe={recipe} note={when} onPick={onPick} />
              ))}
            </Section>
          )}
          <Section title={`All ${plural(dishType)}`}>
            {all.map((r) => (
              <RecipeRow key={r.id} recipe={r} onPick={onPick} />
            ))}
            {all.length === 0 && <p style={{ ...supporting, margin: 0 }}>No {plural(dishType)} in your recipes yet.</p>}
          </Section>
        </>
      )}
    </div>
  );
}

function RecipeRow({ recipe, note, onPick }: { recipe: Recipe; note?: string; onPick: (id: string) => void }) {
  return (
    <PickRow title={recipe.title} detail={recipeMeta(recipe)} onClick={() => onPick(recipe.id)} aside={note && <span style={{ ...supporting, flexShrink: 0 }}>{note}</span>} />
  );
}

// -------------------------------------------------------- leftovers ---

function LeftoversTab({ day, slot, dishType, onPick }: { day: Day; slot: MealSlot; dishType: DishType; onPick: (c: { source: LeftoverRow["candidate"] } | { recipeId: string }) => void }) {
  const mp = useMealPlanner();
  const [older, setOlder] = useState(false);
  const [query, setQuery] = useState("");
  const rows = leftoverRows(mp.plansByWeek, { weekStart: mp.weekStart, day, mealSlot: slot, dishType }, mp.recipeById, new Date());
  const earlier = rows.filter((r) => !r.sameDay);
  const sameDay = rows.filter((r) => r.sameDay);
  const row = (r: LeftoverRow) => (
    <PickRow
      key={`${r.candidate.weekStart}|${r.candidate.day}|${r.candidate.mealSlot}|${r.candidate.dishType}`}
      title={r.title}
      detail={r.from}
      onClick={() => onPick({ source: r.candidate })}
      aside={
        (r.cooked || r.usedOn) && (
          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: SPACE.xs, flexShrink: 0 }}>
            {/* Cooked (slice 7c): linking to it won't scale it up. */}
            {r.cooked && <span style={{ ...badge, color: COLORS.accent, borderColor: COLORS.accent }}>Cooked</span>}
            {r.usedOn && <span style={badge}>Used {r.usedOn.slice(0, 3)}</span>}
          </span>
        )
      }
    />
  );

  if (older) {
    const matches = searchRecipes(mp.recipes, query);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
        <button type="button" onClick={() => setOlder(false)} style={{ ...textButton, alignSelf: "flex-start", fontSize: FONT_SIZE.sm }}>
          ‹ Back to recent meals
        </button>
        <p style={{ ...supporting, margin: 0 }}>Leftovers from the freezer or from before last week. They aren't linked to a meal, so nothing is scaled.</p>
        <input type="search" autoFocus aria-label="Search any recipe" placeholder="Search any recipe" value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inputStyle, width: "100%" }} />
        <Section title={`${matches.length} ${matches.length === 1 ? "recipe" : "recipes"}`}>
          {matches.map((r) => (
            <PickRow key={r.id} title={r.title} detail={RECIPE_TYPE_LABELS[r.recipeType as DishType] ?? ""} onClick={() => onPick({ recipeId: r.id })} />
          ))}
        </Section>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <p style={{ ...supporting, margin: 0 }}>Pick the meal these leftovers come from. A meal still ahead scales up to cover the extra servings. One already eaten or marked cooked stays as it was.</p>
      {earlier.length > 0 && <Section title="From the last 7 days">{earlier.map(row)}</Section>}
      {sameDay.length > 0 && <Section title="Planned earlier this same day">{sameDay.map(row)}</Section>}
      {rows.length === 0 && <p style={{ ...supporting, margin: 0 }}>Nothing planned in the last 7 days to draw on.</p>}
      <PickRow title="Something older" detail="From the freezer or before last week: pick any recipe" onClick={() => setOlder(true)} aside={<span aria-hidden="true">›</span>} />
    </div>
  );
}

// ----------------------------------------------------------- shared ---

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>{title}</h3>
      {children}
    </section>
  );
}

function PickRow({ title, detail, aside, onClick }: { title: string; detail: string; aside?: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: SPACE.xl,
        width: "100%",
        minHeight: LAYOUT.tapTarget + 8,
        border: "none",
        borderBottom: `1px solid ${COLORS.line}`,
        background: "transparent",
        textAlign: "left",
        cursor: "pointer",
        color: COLORS.ink,
        fontSize: FONT_SIZE.base,
        padding: "var(--space-sm) 0",
      }}
    >
      <span style={{ minWidth: 0 }}>
        <span style={{ fontFamily: "var(--font-serif)", fontWeight: 600 }}>{title}</span>
        {detail && <span style={{ ...supporting, display: "block" }}>{detail}</span>}
      </span>
      {aside}
    </button>
  );
}
